import React from 'react';
import { kindShortLabel } from '../../lib/phaseKinds';
import { MobileScreen, MobileHeader, MobileHeaderButton, StatusPill, EmptyState } from '../ui';
import { IconRefresh, IconChevronRight } from '../icons';
import InstallPrompt from './InstallPrompt';
import PushOptInBanner from './PushOptInBanner';
import { formatTime12, formatDateShort, jobStatusPill, combinedLegLabel } from './portalShared';

// ---------------------------------------------------------------------------
// Jobs tab — "Today · Mon, Sep 14 · Marcus Hill · 3 jobs" with refresh, a
// card per job (client · time · StatusPill, one meta line, FROM / TO,
// crew colour dots + "You lead", chevron). Tomorrow's preview is a muted
// group under today's cards. Tapping a card opens the job screen.
// ---------------------------------------------------------------------------

const routeLabelCls = 'text-[10px] font-bold tracking-wider text-slate-400 uppercase';

function CrewDots({ crew, max = 5 }) {
    const shown = crew.slice(0, max);
    return (
        <span className="flex items-center" aria-hidden="true">
            {shown.map((c, i) => (
                <span key={c.id} className={`w-4 h-4 rounded-full border-2 border-white ${i > 0 ? '-ml-1.5' : ''}`}
                    style={{ background: c.display_color || '#6b7280' }} title={c.name} />
            ))}
            {crew.length > max && <span className="ml-1 text-[10px] font-bold text-slate-400">+{crew.length - max}</span>}
        </span>
    );
}

function JobSummaryCard({ job, clients, allCrew, allTrucks, validDate, crewId, onOpen }) {
    const client = clients.find(c => c.id === job.client_id);
    const clientName = client?.name || 'Client';
    const jobType = job.job_label || (job.type || '').replace(/_/g, ' ');
    const jobCrew = (job.crew_ids || []).map(id => allCrew.find(c => c.id === id)).filter(Boolean);
    const jobTrucks = (job.truck_ids || []).map(id => allTrucks.find(t => t.id === id)).filter(Boolean);
    const lead = allCrew.find(c => c.id === job.leader_id);
    const isLead = job.leader_id === crewId;
    const pill = jobStatusPill(job, validDate);
    const tripTag = job.trip_tags && job.trip_tags[validDate];
    const tripLabel = typeof tripTag === 'string' ? tripTag : (tripTag?.tag || null);
    const tripDayIndex = Number(job._trip_day_index) || 0;
    const tripDayTotal = Number(job._trip_day_total) || 0;
    const dayKinds = Array.isArray(job._trip_day_kinds) && job._trip_day_kinds.length > 0
        ? job._trip_day_kinds
        : (job.phase_kind ? [job.phase_kind] : []);
    const hasTripBanner = tripDayIndex > 0 && (tripDayTotal > 1 || dayKinds.length > 1);

    const meta = [jobType, `${jobCrew.length} crew`, jobTrucks.length ? jobTrucks.map(t => t.name).join(', ') : null, job.duration ? `est. ${job.duration}h` : null]
        .filter(Boolean).join(' · ');

    return (
        <section
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
            className={`bg-white rounded-xl border p-3 cursor-pointer active:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/30 ${pill.label === 'Done' ? 'border-emerald-200' : 'border-slate-200'}`}
            aria-label={`${clientName} — open job`}
        >
            {hasTripBanner && (
                <div className="text-[10px] font-bold tracking-wider text-amber-700 uppercase mb-1.5">
                    Day {tripDayIndex} of {tripDayTotal} · {combinedLegLabel(dayKinds, job._trip_destination_address)}
                </div>
            )}
            <div className="flex items-center gap-2 min-w-0">
                <div className="text-[16px] font-bold text-slate-900 truncate flex-1 min-w-0">{clientName}</div>
                <span className="font-mono text-[12px] text-slate-500 tabular-nums flex-shrink-0">{formatTime12(job.time) || 'TBD'}</span>
                <StatusPill label={pill.label} color={pill.color} />
            </div>
            <div className="text-xs text-slate-500 truncate mt-0.5">
                {meta}
                {tripLabel && <span className="ml-1.5 inline-block px-1.5 rounded-md bg-amber-100 text-amber-800 font-semibold">{tripLabel}</span>}
            </div>

            <div className="mt-2.5 grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-1 text-[13px] text-slate-700">
                <span className={`${routeLabelCls} pt-0.5`}>From</span>
                <span className="truncate">{job.location_name ? <strong className="text-slate-900">{job.location_name} — </strong> : null}{job.from_address || 'TBD'}</span>
                <span className={`${routeLabelCls} pt-0.5`}>To</span>
                <span className="truncate">{job.to_address || 'TBD'}</span>
            </div>

            <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex items-center gap-2">
                <CrewDots crew={jobCrew} />
                <span className="text-xs text-slate-500 truncate flex-1 min-w-0">
                    {isLead ? <span className="font-bold text-red-600">You lead</span> : (lead ? `${lead.name} leads` : 'No lead set')}
                </span>
                <IconChevronRight size={18} className="text-slate-400 flex-shrink-0" />
            </div>
        </section>
    );
}

function TomorrowCard({ job, clients, allCrew, crewId }) {
    const c = clients.find(x => x.id === job.client_id);
    const crewNames = (job.crew_ids || []).map(id => allCrew.find(m => m.id === id)?.name).filter(Boolean);
    return (
        <div className="bg-white/70 rounded-xl border border-dashed border-slate-300 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-slate-800 text-[14px] truncate">{c?.name || 'Client'}</span>
                <span className="font-mono text-[12px] text-slate-500 tabular-nums flex-shrink-0">{job.time ? formatTime12(job.time) : 'Time TBD'}</span>
            </div>
            <div className="text-[12px] text-slate-500 truncate">
                {job.job_label || (job.phase_kind ? kindShortLabel(job.phase_kind) : (job.type || '').replace(/_/g, ' '))}
                {crewNames.length > 0 && ` · ${crewNames.join(', ')}`}
                {job.leader_id === crewId ? ' · you lead' : ''}
            </div>
            {job.from_address && <div className="text-[12px] text-slate-500 mt-1 truncate">{job.from_address}</div>}
            {job.to_address && <div className="text-[12px] text-slate-400 truncate">→ {job.to_address}</div>}
        </div>
    );
}

export default function JobsTab({ jobs, tomorrowJobs, tomorrowDate, clients, allCrew, allTrucks, validDate, crewMember, vapidPublicKey, portalToken, refresh, onOpenJob, tabs, strip }) {
    const leadJobs = jobs.filter(j => j.leader_id === crewMember.id);
    const n = jobs.length;
    const sub = `${formatDateShort(validDate)} · ${crewMember.name}${leadJobs.length ? ' (lead)' : ''} · ${n} job${n === 1 ? '' : 's'}`;

    return (
        <MobileScreen
            header={(
                <MobileHeader
                    title="Today"
                    sub={sub}
                    right={<MobileHeaderButton label="Refresh" onClick={refresh}><IconRefresh size={18} /></MobileHeaderButton>}
                />
            )}
            strip={strip}
            tabs={tabs}
        >
            <InstallPrompt />
            {/* Push subscriptions are still keyed by portal token
                (api/crew-portal-push.js). JWT-only sessions have no token,
                so the banner hides until push is re-keyed to crew_id. */}
            {portalToken && <PushOptInBanner vapidPublicKey={vapidPublicKey} token={portalToken} />}

            {jobs.length === 0 ? (
                <EmptyState
                    title="No jobs today"
                    hint={(tomorrowJobs || []).length > 0
                        ? `Nothing today — you're on ${tomorrowJobs.length} job${tomorrowJobs.length === 1 ? '' : 's'} tomorrow (below).`
                        : 'Nothing on the board for you — check back after dispatch.'}
                />
            ) : (
                jobs.map(job => (
                    <JobSummaryCard
                        key={job.id}
                        job={job}
                        clients={clients}
                        allCrew={allCrew}
                        allTrucks={allTrucks}
                        validDate={validDate}
                        crewId={crewMember.id}
                        onOpen={() => onOpenJob(job.id)}
                    />
                ))
            )}

            {(tomorrowJobs || []).length > 0 && (
                <div className="pt-1 space-y-2">
                    <div className="px-1 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                        Tomorrow{tomorrowDate ? ` · ${formatDateShort(tomorrowDate)}` : ''} · preview
                    </div>
                    {tomorrowJobs.map(job => (
                        <TomorrowCard key={job.id} job={job} clients={clients} allCrew={allCrew} crewId={crewMember.id} />
                    ))}
                    <p className="px-1 text-[11px] text-slate-400">Full details + recap unlock tomorrow.</p>
                </div>
            )}
        </MobileScreen>
    );
}
